/**
 * TestModeButton - Developer testing utility
 *
 * When enabled via localStorage flag 'cesim_test_mode', this button:
 * 1. Joins 5 test players to the session
 * 2. Starts the session
 * 3. Shows clickable links to open each player window
 *
 * To enable: localStorage.setItem('cesim_test_mode', 'true')
 * To disable: localStorage.removeItem('cesim_test_mode')
 */

import { useState, useEffect } from 'react';
import { sessionsService } from '@/services/sessions';

interface TestModeButtonProps {
  sessionId: string;
  sessionCode: string;
  sessionStatus: 'WAITING' | 'LIVE' | 'COMPLETE';
  players?: Array<{ playerId: string; displayName: string }>;
  onPlayersJoined?: () => void;
}

interface TestPlayer {
  playerId: string;
  displayName: string;
  url: string;
}

const TEST_PLAYER_NAMES = [
  'TestAlpha',
  'TestBravo',
  'TestCharlie',
  'TestDelta',
  'TestEcho',
];

// Storage key for persisting test players
const TEST_PLAYERS_STORAGE_KEY = 'cesim_test_players';

export function TestModeButton({ sessionId, sessionCode, sessionStatus, players, onPlayersJoined }: TestModeButtonProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string>('');
  const [testPlayers, setTestPlayers] = useState<TestPlayer[]>([]);
  const [simulationComplete, setSimulationComplete] = useState(false);

  // Check if test mode is enabled
  const isTestModeEnabled = localStorage.getItem('cesim_test_mode') === 'true';

  // On mount, check if we have stored test players for this session
  useEffect(() => {
    if (!isTestModeEnabled) return;

    const stored = localStorage.getItem(TEST_PLAYERS_STORAGE_KEY);
    if (stored) {
      try {
        const data = JSON.parse(stored);
        if (data.sessionId === sessionId && data.players?.length > 0) {
          setTestPlayers(data.players);
        }
      } catch (e) {
        // Ignore parse errors
      }
    }
  }, [sessionId, isTestModeEnabled]);

  // Also detect test players from the current roster
  useEffect(() => {
    if (!isTestModeEnabled || !players || testPlayers.length > 0) return;

    // Check if current players include test players
    const detectedTestPlayers = players
      .filter(p => TEST_PLAYER_NAMES.includes(p.displayName))
      .map(p => ({
        playerId: p.playerId,
        displayName: p.displayName,
        url: `/student/role/${sessionId}?testPlayerId=${p.playerId}`,
      }));

    if (detectedTestPlayers.length > 0) {
      setTestPlayers(detectedTestPlayers);
      // Store for persistence
      localStorage.setItem(TEST_PLAYERS_STORAGE_KEY, JSON.stringify({
        sessionId,
        players: detectedTestPlayers,
      }));
    }
  }, [sessionId, players, isTestModeEnabled, testPlayers.length]);

  if (!isTestModeEnabled) {
    return null;
  }

  // Don't show for completed sessions unless simulation just finished
  if (sessionStatus === 'COMPLETE' && !simulationComplete) {
    return null;
  }

  async function handleTestSession() {
    setLoading(true);
    setError(null);
    setStatus('Joining test players...');

    try {
      // Step 1: Join 5 test players
      const joinedPlayers: TestPlayer[] = [];

      for (const name of TEST_PLAYER_NAMES) {
        try {
          const response = await sessionsService.joinSession({
            code: sessionCode,
            displayName: name,
            fullName: `${name} (Test)`,
          });
          const url = `/student/role/${sessionId}?testPlayerId=${response.playerId}`;
          joinedPlayers.push({
            playerId: response.playerId,
            displayName: response.displayName,
            url,
          });
          setStatus(`Joined ${joinedPlayers.length}/${TEST_PLAYER_NAMES.length} players...`);
        } catch (err: any) {
          // If player already joined or other error, continue
          console.warn(`Failed to join player ${name}:`, err);
        }
      }

      if (joinedPlayers.length === 0) {
        throw new Error('Failed to join any test players');
      }

      setTestPlayers(joinedPlayers);

      // Store test players in localStorage for persistence across re-renders
      localStorage.setItem(TEST_PLAYERS_STORAGE_KEY, JSON.stringify({
        sessionId,
        players: joinedPlayers,
      }));

      // Notify parent to refresh roster
      onPlayersJoined?.();

      // Step 2: Start the session
      setStatus('Starting session...');
      await sessionsService.startSession(sessionId);

      // Refresh parent after session started
      onPlayersJoined?.();

      // Step 3: Auto-play the entire game
      setStatus('Simulating game (all questions)...');
      try {
        const simResult = await sessionsService.simulateGame(sessionId);
        console.log('Simulation result:', simResult);

        if (simResult.gameStatus === 'completed') {
          setSimulationComplete(true);
          setStatus(`Game completed! ${simResult.totalSubmissions} decisions submitted.`);
        } else {
          setStatus(`Simulation finished (status: ${simResult.gameStatus}). ${simResult.totalSubmissions} decisions submitted.${simResult.errors?.length ? ` ${simResult.errors.length} errors.` : ''}`);
        }
      } catch (simErr: any) {
        console.error('Simulation failed:', simErr);
        setStatus('Session started but simulation failed. You can play manually.');
        setError(simErr.response?.data?.message || simErr.message || 'Simulation failed');
      }

      // Refresh parent to reflect completed state
      onPlayersJoined?.();

    } catch (err: any) {
      console.error('Test session failed:', err);
      setError(err.response?.data?.message || err.message || 'Test session failed');
    } finally {
      setLoading(false);
    }
  }

  // Show player buttons if we have test players (either from running test or detected from roster)
  const showPlayerButtons = testPlayers.length > 0;
  const showRunButton = sessionStatus === 'WAITING' && !showPlayerButtons;

  return (
    <div className="bg-[#DD6832]/10 border-2 border-[#DD6832]/40 border-dashed rounded-xl px-6 py-4 mb-6">
      <div className="flex items-center gap-3 mb-3">
        <span className="text-[#c4562a] text-lg">🧪</span>
        <h3 className="text-sm font-bold text-[#c4562a]">Developer Test Mode</h3>
      </div>

      {simulationComplete && (
        <div className="flex items-center gap-2 mb-3 px-3 py-2 bg-[#367A25]/10 border border-[#367A25]/30 rounded-lg">
          <span className="text-[#367A25] text-sm font-semibold">Game simulation complete — session is COMPLETE</span>
        </div>
      )}

      {showRunButton && (
        <>
          <p className="text-xs text-[#c4562a] mb-3">
            This will join 5 test players and start the session.
          </p>

          <button
            onClick={handleTestSession}
            disabled={loading}
            className="px-4 py-2 bg-[#367A25] text-white font-semibold rounded-lg hover:bg-[#2d6820] disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
          >
            {loading ? 'Running...' : 'Run Test Session'}
          </button>
        </>
      )}

      {showPlayerButtons && (
        <>
          <p className="text-xs text-[#c4562a] mb-3">
            {testPlayers.length} test players ready. Click each button to open a player window.
          </p>

          <div className="flex flex-wrap gap-2">
            {testPlayers.map((player, index) => (
              <button
                key={player.playerId}
                onClick={() => window.open(player.url, `test_${player.playerId}`, 'width=1200,height=800')}
                className="flex items-center gap-2 px-4 py-2 bg-[#12356B] text-white rounded-lg hover:bg-[#0d2850] transition-colors text-sm font-medium"
              >
                <span className="w-5 h-5 flex items-center justify-center bg-[#0d2850] rounded-full text-xs">
                  {index + 1}
                </span>
                {player.displayName}
              </button>
            ))}
          </div>

          <p className="mt-3 text-xs text-[#c4562a]">
            Click each button one at a time. If popups are blocked, allow popups for this site.
          </p>

          <button
            onClick={() => {
              localStorage.removeItem(TEST_PLAYERS_STORAGE_KEY);
              setTestPlayers([]);
            }}
            className="mt-2 text-xs text-[#12356B] underline hover:text-[#0d2850]"
          >
            Clear test players
          </button>
        </>
      )}

      {status && (
        <p className="mt-3 text-xs text-[#c4562a]">{status}</p>
      )}

      {error && (
        <p className="mt-2 text-xs text-[#c4562a]">{error}</p>
      )}

      <p className="mt-3 text-xs text-slate-600">
        Disable: <code className="bg-[#E2E5EC] px-1 rounded text-[#12356B]">localStorage.removeItem('cesim_test_mode')</code>
      </p>
    </div>
  );
}
